import type { CSSProperties } from 'react';

import { GlassSurface } from '@components/glass/GlassSurface';
import { Chip } from '@components/ui/Chip';
import { WORK_ITEMS } from '@config/content';
import { cx } from '@utils/cx';

import styles from './WorkGrid.module.scss';

interface WorkGridProps {
  /** Show only the first n items, as the home page does. */
  readonly limit?: number;
}

type HueStyle = CSSProperties & Record<'--hue-a' | '--hue-b', string>;

/**
 * Project cards. Each has an abstract artwork panel tinted with the
 * project's two hues, with the card's glass layered over it, so every card
 * refracts a different colour.
 */
export const WorkGrid = ({ limit }: WorkGridProps) => (
  <ul role="list" className={styles.grid}>
    {WORK_ITEMS.slice(0, limit).map((item, index) => {
      const hueStyle: HueStyle = { '--hue-a': item.hues[0], '--hue-b': item.hues[1] };

      return (
        <li
          key={item.id}
          className={cx(styles.item, index === 0 && styles.featured)}
          style={hueStyle}
        >
          <div className={styles.art} aria-hidden="true">
            <span />
            <span />
          </div>
          <GlassSurface as="article" className={styles.card} data-reveal="">
            <p className={styles.sector}>{item.sector}</p>
            <h3 className={styles.title}>{item.title}</h3>
            <p className={styles.summary}>{item.summary}</p>
            <ul role="list" className={styles.stack} aria-label="Technologies">
              {item.stack.map((tech) => (
                <li key={tech}>
                  <Chip>{tech}</Chip>
                </li>
              ))}
            </ul>
          </GlassSurface>
        </li>
      );
    })}
  </ul>
);
